// Material Issue/Receipt Challans + live Material Register for the job-work
// portal. Reused in two places, scoped by company:
//   • Main portal (JobWork page) — RSR for RSR users; admin gets a RSR/Deroi toggle.
//   • Deroi module (Procurement deroiMode) — locked to Deroi.
// Pass lockedCompany='rsr'|'deroi' to fix the scope (no toggle); pass null to let
// admin toggle. The server independently enforces the per-role scope.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { printDocument, printBtnStyle } from '../utils/print';
import { ListFooter } from '../ui';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
const tag = (t) => ({ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: t.bg, color: t.c, whiteSpace: 'nowrap' });
const inputStyle = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const btnPrimary = { padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
const PROC_LABEL = { embroidery: 'Embroidery', printing: 'Printing', washing: 'Washing', special_finishing: 'Special Finishing', knitting: 'Knitting', dyeing: 'Dyeing', finishing: 'Finishing' };
const CHALLAN_ROLES = ['admin', 'management', 'procurement_officer', 'merchandiser', 'deroi_operator'];
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// GST math for a valued Issue challan. taxable value is per line (qty × rate);
// subtotal = Σ; a single rate is split half/half into CGST+SGST for the printout.
// cgst is rounded and sgst = gst − cgst, so the two halves always re-sum exactly.
function challanGst(items, gstPct) {
  const subtotal = +((items || []).reduce((s, it) => s + num(it.qty_sent) * num(it.rate), 0)).toFixed(2);
  const pct = num(gstPct);
  const gstAmount = +(subtotal * pct / 100).toFixed(2);
  const cgst = +(gstAmount / 2).toFixed(2);
  return { subtotal, pct, gstAmount, cgst, sgst: +(gstAmount - cgst).toFixed(2), grandTotal: +(subtotal + gstAmount).toFixed(2) };
}

// One label/value row in the GST summary box.
function Row2({ label, value, bold }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontWeight: bold ? 700 : 500, color: bold ? '#166534' : '#444' }}>
    <span>{label}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>;
}

// Export the Material Register summary to CSV.
function exportRegisterCsv(rows, company) {
  const head = ['Job-Worker', 'JWO No', 'GPO', 'Item / Material', 'Unit', 'Total Issued', 'Total Received', 'Outstanding Balance'];
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const body = rows.map(r => [r.vendor_name, r.jwo_no, r.gpo_no, r.item, r.unit, num(r.total_issued), num(r.total_received), num(r.balance_outstanding)].map(esc).join(','));
  const csv = [head.join(','), ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `material-register-${company}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// Gate-pass style PDF for an issue/receipt challan (travels with the goods).
function printChallan(c) {
  const isReceipt = c.type === 'receipt';
  const items = c.items || [];
  const gst = challanGst(items, c.gst_pct);
  const cols = isReceipt
    ? ['#', 'Item', 'Description', 'GSM/Dia', 'Shade', 'Lot', 'Rolls', 'Unit', 'Qty Sent', 'Qty Recd', 'Balance']
    : ['#', 'Item', 'Description', 'GSM/Dia', 'Shade', 'Lot', 'Rolls', 'Unit', 'Qty Sent', 'Rate ₹', 'Value ₹'];
  const rows = items.map((it, i) => {
    const base = [i + 1, it.item, it.description || '—', it.gsm_dia || '—', it.shade || '—', it.lot_no || '—', it.no_of_rolls || '—', it.unit || '—', num(it.qty_sent).toLocaleString('en-IN')];
    return isReceipt
      ? [...base, num(it.qty_received).toLocaleString('en-IN'), num(it.balance) > 0 ? `${num(it.balance).toLocaleString('en-IN')} short` : '✓ nil']
      : [...base, num(it.rate) ? inr(it.rate) : '—', inr(num(it.qty_sent) * num(it.rate))];
  });
  const gateFields = [
    ['Challan No', c.challan_no],
    ['Type', isReceipt ? 'RECEIPT (material received back)' : 'ISSUE (material sent out)'],
    ['Job Worker / Vendor', c.vendor_name],
    ['JWO No', c.jwo_no], ['GPO', c.gpo_no],
    ...(isReceipt
      ? [['Against Issue Challan', c.linked_issue_challan_no], ['Received By', c.received_by]]
      : [['Delivery From', c.delivery_from], ['Against Challan', c.against_challan], ['Delivery Date', fmtD(c.delivery_date)]]),
    ['Transport (carrier)', c.transport], ['Vehicle No', c.vehicle_no],
    ['Remarks', c.remarks],
  ];
  // THE VESSEL, FROM THE LOT — not from the line's note. The shade is what makes the load one lot,
  // and this paper is the only thing the dyer reads; a control that lives only in the system means the
  // vessel gets set from a phone call. process_lot is read from the linked lot (lots.source_jw_challan_id),
  // so it cannot disagree with what the server recorded. Shown first, above the gate-pass details.
  const pl = c.process_lot;
  const lotSection = pl ? [{ title: pl.lot_type === 'dyeing' ? 'Dye Lot — run this load at' : `${pl.lot_type[0].toUpperCase()}${pl.lot_type.slice(1)} Lot`,
    fields: [
      [pl.lot_type === 'dyeing' ? 'Dye Lot No' : 'Lot No', pl.lot_no],
      ...(pl.shade_name ? [['Dye Shade', pl.shade_name]] : []),
      ...(pl.machine_name ? [['Machine', `${String(pl.machine_name).trim()}${pl.loading_capacity_kg ? ` (loads ${num(pl.loading_capacity_kg)} kg)` : ''}`]] : []),
    ] }] : [];
  const sections = [
    ...lotSection,
    { title: 'Gate Pass Details', fields: gateFields },
    { title: 'Material', table: { columns: cols, rows } },
  ];
  // Tax summary — Issue only (a receipt is not a tax document we issue).
  if (!isReceipt && (gst.subtotal > 0 || gst.pct > 0)) {
    sections.push({ title: 'Tax Summary', fields: [
      ['Subtotal (taxable value)', inr(gst.subtotal)],
      [`CGST @ ${gst.pct / 2 || 0}%`, inr(gst.cgst)],
      [`SGST @ ${gst.pct / 2 || 0}%`, inr(gst.sgst)],
      ['Grand Total', inr(gst.grandTotal)],
    ] });
  }
  sections.push({ fields: [['', ''], ['Issued by / Sign', '\n\n______________________'], ['Received by / Sign', '\n\n______________________']] });
  printDocument({
    docTitle: isReceipt ? 'Material Receipt Challan' : 'Material Issue Challan',
    subtitle: `${c.challan_no} · ${(c.jwo_type || '').toUpperCase()} Job Work`,
    meta: [[isReceipt ? 'Date' : 'Date of Pick up', fmtD(c.challan_date)], ['Status', (c.status || '').toUpperCase()], ['JWO', c.jwo_no || '—'],
           ...(pl ? [[pl.lot_type === 'dyeing' ? 'Dye Lot' : 'Lot', `${pl.lot_no}${pl.shade_name ? ` · ${pl.shade_name}` : ''}`]] : [])],
    sections,
  });
}

export default function JobWorkChallans({ lockedCompany = null }) {
  const { user } = useAuth();
  const canManage = CHALLAN_ROLES.includes(user?.role);
  const [company, setCompany] = useState(lockedCompany || 'rsr');
  const navigate = useNavigate();
  // JWO creation lives on the Job-Work page; deep-link to its <company> tab with
  // the New JWO modal open (JobWork.js honours ?tab= & ?new=1). Reuses that flow.
  const goNewJwo = () => navigate('/job-work?tab=' + company + '&new=1');
  const [view, setView] = useState('challans'); // 'challans' | 'register'
  const [filters, setFilters] = useState({});
  const [challans, setChallans] = useState([]);
  const [register, setRegister] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deroiVendors, setDeroiVendors] = useState([]);
  const [jwos, setJwos] = useState([]);
  const [regOpen, setRegOpen] = useState(null);
  const [showIssue, setShowIssue] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  // MOVE A MISPLACED ISSUE CHALLAN (mig 419; Rajan, 27 Oct 2026). Admin/management, with a reason. Its receipts, lots
  // and gate passes go with it, and both orders are re-measured. Only orders for the same work are offered; the server
  // refuses anything else in words, and refuses while a receipt is held or either order has a live invoice.
  const canMove = ['admin', 'management'].includes(user?.role);
  const moveChallan = async (c) => {
    const from = jwos.find(j => j.id === c.jwo_id);
    const options = jwos.filter(j => j.id !== c.jwo_id && j.status !== 'cancelled' && (!from || (j.gpo_no === from.gpo_no
      && j.process_type === from.process_type && (j.vendor_id || '') === (from.vendor_id || '') && (j.deroi_vendor_id || '') === (from.deroi_vendor_id || '')
      && Number(j.rate) === Number(from.rate))));
    if (!options.length) { alert(`No other order for the same GPO, process, job-worker and rate as ${c.jwo_no}. A challan moves only between orders for the same work.`); return; }
    const pick = window.prompt(`Move ${c.challan_no} from ${c.jwo_no} to which order?\n\n`
      + options.map((j, i) => `${i + 1}. ${j.jwo_no} — ${j.status}, header ${num(j.qty_sent).toLocaleString('en-IN')} ${j.unit || ''}`).join('\n')
      + `\n\nIts receipt challans, lots and gate passes move with it. Type the number:`, '');
    if (pick === null) return;
    const to = options[Number(pick) - 1];
    if (!to) { alert('Type one of the numbers shown.'); return; }
    const why = window.prompt(`${c.challan_no}: ${c.jwo_no} → ${to.jwo_no}.\n\nBoth orders' received and shortage are re-measured from their lots; excess is not changed. `
      + `The move is recorded permanently on both orders.\n\nWhy does it belong to ${to.jwo_no}?`, '');
    if (why === null) return;
    if (why.trim().length < 5) { alert('Say why, in a few words. It is kept on the record of the move.'); return; }
    try {
      const r = await api.post(`/jobwork/challans/${c.id}/reassign`, { to_jwo_id: to.id, reason: why.trim() });
      const d = r.data.data;
      alert(`${c.challan_no} is now on ${to.jwo_no}.\n${d.from_jwo_no}: received ${d.from_received_was ?? '—'} → ${d.from_received_now ?? '—'}, shortage ${d.from_shortage_was ?? '—'} → ${d.from_shortage_now}.`
        + `\n${d.to_jwo_no}: received ${d.to_received_was ?? '—'} → ${d.to_received_now ?? '—'}, shortage ${d.to_shortage_was ?? '—'} → ${d.to_shortage_now}.`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  // Keep the active company in sync if the lock changes (placement-driven).
  useEffect(() => { if (lockedCompany) setCompany(lockedCompany); }, [lockedCompany]);

  const load = useCallback(async () => {
    const qs = new URLSearchParams({ jwo_type: company });
    // §C — BOTH modes now filter on an FK. Deroi challans carry deroi_vendor_id
    // as of mig 177, so the dropdown sends an id in either mode and a vendor whose
    // name later drifts is still found. filters.vendor_id holds an id for both.
    if (filters.vendor_id) qs.set(company === 'deroi' ? 'deroi_vendor_id' : 'vendor_id', filters.vendor_id);
    if (filters.jwo_no) qs.set('jwo_no', filters.jwo_no);
    if (filters.item) qs.set('item', filters.item);
    const [c, r] = await Promise.all([
      api.get('/jobwork/challans?' + qs.toString()).catch(() => ({ data: { data: [] } })),
      api.get('/jobwork/register?' + qs.toString()).catch(() => ({ data: { data: [] } })),
    ]);
    setChallans(c.data.data || []); setRegister(r.data.data || []);
  }, [company, filters]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/suppliers').then(r => setSuppliers((r.data.data || []).filter(s => s.is_active !== false))).catch(() => {});
  }, []);
  // Deroi mode's vendor filter reads deroi_vendors (all ACTIVE, not just approved —
  // this is a read filter over historical challans, a vendor may have challans yet
  // no longer be approved). RSR mode keeps using `suppliers` above.
  useEffect(() => {
    if (company !== 'deroi') { setDeroiVendors([]); return; }
    api.get('/procurement/deroi-vendors?active=true').then(r => setDeroiVendors(r.data.data || [])).catch(() => setDeroiVendors([]));
  }, [company]);
  useEffect(() => {
    setRegOpen(null);
    // Drop any selected vendor when the company toggles — the id space differs
    // (a suppliers id vs a deroi_vendors id), so a stale value can't match the new mode.
    setFilters(f => (f.vendor_id ? { ...f, vendor_id: '' } : f));
    api.get('/jobwork?type=' + company).then(r => setJwos(r.data.data || [])).catch(() => setJwos([]));
  }, [company]);

  const companyBtn = (co) => (
    <button key={co} onClick={() => setCompany(co)} style={{
      padding: '6px 14px', borderRadius: 6, border: '1px solid ' + (company === co ? '#3B3A8F' : '#ddd'),
      background: company === co ? '#3B3A8F' : 'white', color: company === co ? 'white' : '#666',
      fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{co === 'rsr' ? '🏢 RSR' : '🏭 Deroi'}</button>
  );
  const viewBtn = (key, label) => (
    <button key={key} onClick={() => setView(key)} style={{
      padding: '7px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13,
      fontWeight: view === key ? 700 : 400, color: view === key ? '#3B3A8F' : '#888',
      borderBottom: view === key ? '2px solid #3B3A8F' : '2px solid transparent' }}>{label}</button>
  );
  const filterBar = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
      <select style={inputStyle} value={filters.vendor_id || ''} onChange={e => setFilters({ ...filters, vendor_id: e.target.value })}>
        <option value="">All job-workers</option>
        {company === 'deroi'
          ? deroiVendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)
          : suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <input style={inputStyle} placeholder="JWO no…" value={filters.jwo_no || ''} onChange={e => setFilters({ ...filters, jwo_no: e.target.value })} />
      <input style={inputStyle} placeholder="Item / material…" value={filters.item || ''} onChange={e => setFilters({ ...filters, item: e.target.value })} />
      {(filters.vendor_id || filters.jwo_no || filters.item) && <button onClick={() => setFilters({})} style={{ ...inputStyle, cursor: 'pointer', background: '#f0f2f8' }}>✕ Clear</button>}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, borderBottom: '1px solid #eee', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!lockedCompany && <div style={{ display: 'flex', gap: 6, marginRight: 6 }}>{['rsr', 'deroi'].map(companyBtn)}</div>}
          {viewBtn('challans', '📋 Challans')}
          {viewBtn('register', '📖 Material Register')}
        </div>
        {view === 'challans' && canManage && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowIssue(true)} style={{ ...btnPrimary, background: '#E22D2D' }}>📤 Issue Challan</button>
            <button onClick={() => setShowReceipt(true)} style={btnPrimary}>📥 Receipt Challan</button>
          </div>
        )}
        {view === 'register' && (
          <button onClick={() => exportRegisterCsv(register, company)} style={{ ...btnPrimary, background: '#166534' }}>⬇ Export CSV</button>
        )}
      </div>

      {jwos.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, color: '#9A3412' }}>
          <span>No {company.toUpperCase()} job-work orders yet — a challan is raised against a JWO, so create one in Job-Work first.</span>
          {canManage && <button onClick={goNewJwo} style={{ ...btnPrimary, background: '#EA580C', whiteSpace: 'nowrap' }}>+ New JWO</button>}
        </div>
      )}

      {filterBar}

      {view === 'challans' && (<>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['Challan No', 'Type', 'Date', 'JWO', 'Job-Worker', 'Items', 'Qty Sent', 'Qty Recd', 'Status', ''].map(h => (
              <th key={h} style={{ padding: '9px 8px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {challans.length === 0 ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No material challans yet for {company.toUpperCase()}.</td></tr>
              : challans.map(c => {
                const sent = c.items.reduce((s, it) => s + num(it.qty_sent), 0);
                const rec = c.items.reduce((s, it) => s + num(it.qty_received), 0);
                const isR = c.type === 'receipt';
                return (
                  // A cancelled challan stays in the register — it is the record of what was
                  // keyed — but it should not read like a live one. The Status column already
                  // said so; the row did not, and the register is scanned by row.
                  <tr key={c.id} style={{ borderBottom: '1px solid #f5f5f5', opacity: c.status === 'cancelled' ? 0.55 : 1, background: c.status === 'cancelled' ? '#FAFAFA' : 'transparent' }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 700, color: isR ? '#166534' : '#E22D2D' }}>{c.challan_no}{c.gate_pass_no && <div style={{ fontSize: 9, color: '#3B3A8F', fontWeight: 600 }} title="Linked gate pass">🚪 {c.gate_pass_no}</div>}</td>
                    <td style={{ padding: '8px' }}><span style={tag(isR ? { bg: '#EAF7EE', c: '#166534' } : { bg: '#FCEBEB', c: '#A32D2D' })}>{isR ? '📥 Receipt' : '📤 Issue'}</span></td>
                    <td style={{ padding: '8px' }}>{fmtD(c.challan_date)}</td>
                    <td style={{ padding: '8px', fontFamily: 'monospace', color: '#3B3A8F' }}>{c.jwo_no || '—'}</td>
                    <td style={{ padding: '8px' }}>{c.vendor_name}</td>
                    <td style={{ padding: '8px', color: '#666' }}>{c.items.map(it => it.item).join(', ') || '—'}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{sent ? sent.toLocaleString('en-IN') : '—'}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: isR && rec < sent ? '#A32D2D' : '#166534' }}>{isR ? rec.toLocaleString('en-IN') : '—'}</td>
                    <td style={{ padding: '8px' }}><span style={tag(c.status === 'received' ? { bg: '#EAF7EE', c: '#166534' } : c.status === 'cancelled' ? { bg: '#f0f0f0', c: '#888' } : { bg: '#FFF8E6', c: '#9A6B00' })}>{(c.status || '').toUpperCase()}</span></td>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}><button onClick={() => printChallan(c)} style={printBtnStyle}>🖨 Print</button>
                      {canMove && !isR && c.status !== 'cancelled' && c.jwo_id && <button onClick={() => moveChallan(c)} style={{ ...printBtnStyle, marginLeft: 6 }} title="Raised against the wrong order? Move it, with its receipts, lots and gate passes">Move to another order…</button>}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <ListFooter shown={challans.length} total={challans.length} noun="challans" />
      </>)}

      {view === 'register' && (
        <div>
          <div style={{ marginBottom: 10, padding: '8px 12px', background: '#EEEDF8', borderRadius: 6, fontSize: 12, color: '#3B3A8F' }}>
            📖 Outstanding = total issued − total received (material still with the job-worker). Click a row to see the challans behind it.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>
              {['Job-Worker', 'JWO', 'GPO', 'Item / Material', 'Unit', 'Issued', 'Received', 'Outstanding', ''].map(h => (
                <th key={h} style={{ padding: '9px 8px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {register.length === 0 ? <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No register entries yet for {company.toUpperCase()}.</td></tr>
                : register.map((row, idx) => {
                  const key = `${row.vendor_id}|${row.jwo_no}|${row.item}`;
                  const open = regOpen === key;
                  const out = num(row.balance_outstanding);
                  const drill = open ? challans
                    .filter(c => c.vendor_id === row.vendor_id && c.jwo_no === row.jwo_no)
                    .flatMap(c => c.items.filter(it => it.item === row.item).map(it => ({ c, it }))) : [];
                  return (
                    <React.Fragment key={key + idx}>
                      <tr onClick={() => setRegOpen(open ? null : key)} style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: open ? '#FBFBFE' : 'white' }}>
                        <td style={{ padding: '8px', fontWeight: 600 }}>{open ? '▾ ' : '▸ '}{row.vendor_name}</td>
                        <td style={{ padding: '8px', fontFamily: 'monospace', color: '#3B3A8F' }}>{row.jwo_no || '—'}</td>
                        <td style={{ padding: '8px', fontFamily: 'monospace', color: '#E22D2D' }}>{row.gpo_no || '—'}</td>
                        <td style={{ padding: '8px' }}>{row.item}</td>
                        <td style={{ padding: '8px' }}>{row.unit || '—'}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{num(row.total_issued).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{num(row.total_received).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: out > 0 ? '#A32D2D' : '#166534' }}>{out.toLocaleString('en-IN')}{out > 0 ? ' ⚠' : ' ✓'}</td>
                        <td />
                      </tr>
                      {open && (
                        <tr><td colSpan={9} style={{ padding: '0 8px 10px 24px', background: '#FBFBFE' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead><tr style={{ color: '#888' }}>
                              {['Challan', 'Type', 'Date', 'Qty Sent', 'Qty Recd', 'Balance'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600 }}>{h}</th>)}
                            </tr></thead>
                            <tbody>
                              {drill.length === 0 ? <tr><td colSpan={6} style={{ padding: 8, color: '#aaa' }}>No challan entries.</td></tr>
                                : drill.map(({ c, it }, j) => (
                                  <tr key={j} style={{ borderTop: '1px solid #eef' }}>
                                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: c.type === 'receipt' ? '#166534' : '#E22D2D' }}>{c.challan_no}</td>
                                    <td style={{ padding: '5px 8px' }}>{c.type === 'receipt' ? '📥 Receipt' : '📤 Issue'}</td>
                                    <td style={{ padding: '5px 8px' }}>{fmtD(c.challan_date)}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>{c.type === 'issue' ? num(it.qty_sent).toLocaleString('en-IN') : '—'}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>{c.type === 'receipt' ? num(it.qty_received).toLocaleString('en-IN') : '—'}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right', color: num(it.balance) > 0 ? '#A32D2D' : '#166534' }}>{c.type === 'receipt' ? num(it.balance).toLocaleString('en-IN') : '—'}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
          <ListFooter shown={register.length} total={register.length} noun="entries" />
        </div>
      )}

      {showIssue && <IssueChallanModal company={company} jwos={jwos.filter(j => j.type === company)} canManage={canManage} onNewJwo={goNewJwo}
        onClose={() => setShowIssue(false)} onSaved={(c) => { setShowIssue(false); load(); if (c) printChallan(c); }} />}
      {showReceipt && <ReceiptChallanModal company={company}
        onClose={() => setShowReceipt(false)} onSaved={(c) => { setShowReceipt(false); load(); if (c) printChallan(c); }} />}
    </div>
  );
}

// ── Issue Challan: pick a JWO → items auto-fill → qty sent ──
function IssueChallanModal({ company, jwos, canManage, onNewJwo, onClose, onSaved }) {
  const [jwoId, setJwoId] = useState('');
  // BLANK, NOT TODAY. This defaulted to new Date(), and every one of the sixteen challans
  // keyed so far carries challan_date = the day it was typed — the despatch date is
  // nowhere in the record. The team is entering past movements, so the date has to be a
  // decision rather than an accepted default. delivery_date stays blank AND optional:
  // it means "expected back by", and forcing a guess on a retrospective entry is how you
  // get a filled field that is wrong.
  const [date, setDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [transport, setTransport] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [gstPct, setGstPct] = useState('');
  const [againstChallan, setAgainstChallan] = useState('');
  const [deliveryFrom, setDeliveryFrom] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const jwo = jwos.find(j => j.id === jwoId);
  // THE YARN THAT LEFT, NOT A DESCRIPTION OF IT (mig 294). A knitting issue picks from the same
  // pool the Issue Yarn panel offers, scoped to this ORDER's company — a Deroi operator's own
  // company is RSR, so a viewer-scoped pool would be empty for exactly the people using this form.
  const [pool, setPool] = useState([]);
  const [poolApplies, setPoolApplies] = useState(false);
  useEffect(() => {
    setPool([]); setPoolApplies(false);
    if (!jwoId) return;
    api.get(`/jobwork/${jwoId}/issuable-yarn`)
      .then(r => { setPool(r.data.data || []); setPoolApplies(!!r.data.applies); })
      .catch(() => {});
  }, [jwoId]);
  // THE FABRIC THAT LEFT, AND THE VESSEL IT LOADS (stage 6d, mig 300 — the screen it never got).
  // A Deroi dyeing / washing / finishing issue draws on named lots: the server refuses a line that
  // does not say which greige (or dyed) lot it comes from, and a DYEING issue that does not say the
  // shade the vessel runs at. From 18 Sep to 2 Oct no screen sent either, so every dyeing issue was
  // refused — with a message asking for a shade the operator had already typed into the line's
  // free-text Shade note. GET /issuable-greige (also never called until now) carries the lots, their
  // QC verdicts and the GPO's shades.
  const [fabric, setFabric] = useState({ applies: false, members: [], machines: [], draws_on: [], makes: '' });
  const [shadeNames, setShadeNames] = useState({});
  const [shadeId, setShadeId] = useState('');
  const [machineId, setMachineId] = useState('');
  const needsShade = fabric.applies && fabric.makes === 'dyeing';
  useEffect(() => {
    setFabric({ applies: false, members: [], machines: [], draws_on: [], makes: '' }); setShadeId(''); setMachineId('');
    if (!jwoId) return;
    api.get(`/jobwork/${jwoId}/issuable-greige`)
      .then(r => setFabric({ applies: !!r.data.applies, members: r.data.data || [], machines: r.data.machines || [],
                             draws_on: r.data.draws_on || [], makes: r.data.makes || '' }))
      .catch(e => setError(`Could not load the fabric available to this order — ${e.response?.data?.error || e.message}. Reload before issuing.`));
  }, [jwoId]);
  useEffect(() => {
    if (!needsShade || Object.keys(shadeNames).length) return;
    api.get('/procurement/shades').then(r => setShadeNames(Object.fromEntries((r.data.data || []).map(x => [x.id, x.name]))))
      .catch(() => {});   // names only: the picker still works on ids if this fails
  }, [needsShade]); // eslint-disable-line react-hooks/exhaustive-deps
  // The vessel's shade must be a clean FRS shade of EVERY GPO in the load (dyeLot.checkLoad), so
  // the picker offers only those. One option — GPO 1737 has only Lime Stone — is chosen for them.
  const shadeOptions = [...new Set(fabric.members.flatMap(m => m.shade_options || []))];
  // Auto-picked or chosen, the shade's NAME goes onto each line's printout note (unless someone typed
  // their own), so the line and the printed Dye Lot section agree.
  const applyShade = (v) => { setShadeId(v);
    setItems(its => its.map(it => (!it.shade || it.shade_auto) ? { ...it, shade: v ? shadeName(v) : '', shade_auto: true } : it)); };
  useEffect(() => { if (needsShade && shadeOptions.length === 1 && !shadeId) applyShade(String(shadeOptions[0])); },
    [needsShade, shadeOptions.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  // Names arrive after the auto-pick; re-apply so an auto-filled note reads "Lime Stone", not "shade #9".
  useEffect(() => { if (shadeId) setItems(its => its.map(it => it.shade_auto ? { ...it, shade: shadeName(shadeId) } : it)); },
    [shadeNames]); // eslint-disable-line react-hooks/exhaustive-deps
  const shadeName = (id) => shadeNames[id] || `shade #${id}`;
  // Why a lot cannot be drawn — the client's copy of dyeLot.drawRefusal, so the operator meets the
  // QC gate in the picker rather than after Issue & Print. null = drawable.
  const memberBlock = (m) => {
    if (!m || m.lot_type !== 'greige') return null;
    if (!m.qc_result) return 'no QC verdict yet — Quality → Greige QC';
    if (m.qc_result !== 'pass' && !m.qc_released_by) return `judged ${m.qc_result.toUpperCase()} — held until released in Greige QC`;
    return null;
  };
  const memberOf = (it) => fabric.members.find(m => m.id === it.greige_member_id);
  const memberLabel = (m) => `${m.lot_no} · GPO ${m.gpo_no} ${m.component} · ${num(m.available_kg).toLocaleString('en-IN')} kg`;

  const poolKey = (p) => `${p.gpo_no}|${p.sourcing_line_id}`;
  const poolOf = (it) => pool.find(p => poolKey(p) === `${it.gpo_no}|${it.sourcing_line_id}`);
  const pickYarn = (i, key) => {
    const p = pool.find(x => poolKey(x) === key);
    if (!p) { setItem(i, { gpo_no: '', sourcing_line_id: '', item: '' }); return; }
    setItem(i, {
      gpo_no: p.gpo_no, sourcing_line_id: p.sourcing_line_id,
      item: p.item_desc || [p.yarn_count, p.yarn_blend].filter(Boolean).join(' ') || 'Yarn',
      shade: p.shade || '', unit: p.unit || 'KG', material_kind: 'yarn',
    });
  };

  const blankItem = () => ({ item: '', description: '', unit: company === 'rsr' ? 'PCS' : 'KG', qty_sent: '', rate: '', no_of_rolls: '', shade: '', lot_no: '', gsm_dia: '',
    gpo_no: '', sourcing_line_id: '', material_kind: '' });
  useEffect(() => {
    if (!jwo) { setItems([]); return; }
    setItems([{ ...blankItem(), item: jwo.material_description || `${jwo.process_type} material`, unit: jwo.unit || (company === 'rsr' ? 'PCS' : 'KG'), qty_sent: jwo.qty_sent ?? '' }]);
  }, [jwoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setItem = (i, patch) => setItems(its => its.map((it, j) => j === i ? { ...it, ...patch } : it));
  const gst = challanGst(items, gstPct);
  const save = async () => {
    if (!jwoId) { setError('Select a JWO'); return; }
    // These modals are not <form>s, so the `required` attribute never fires on its own.
    if (!date) { setError('Date of Pick up is required — the date the material physically left, not today.'); return; }
    const clean = items.filter(it => (it.item || '').trim() && num(it.qty_sent) > 0);
    if (!clean.length) { setError('Add at least one item with a quantity'); return; }
    if (poolApplies) {
      // A knitting issue must say WHICH yarn left: the greige lot it comes back as cannot exist
      // without it. The server refuses this too — this is so the answer arrives before the save.
      if (clean.some(it => !it.sourcing_line_id)) {
        setError('Pick the yarn for every line — a knitting issue has to say which yarn left, or no greige lot can be created when it comes back.'); return;
      }
      const over = clean.find(it => { const p = poolOf(it); return p && num(it.qty_sent) > num(p.available_kg); });
      if (over) { const p = poolOf(over); setError(`Only ${num(p.available_kg).toLocaleString('en-IN')} kg of that yarn is available for GPO ${p.gpo_no}.`); return; }
    }
    if (fabric.applies) {
      // Each refusal names the line and the field to fix — never "a shade is needed" to someone
      // who has typed one.
      const kind = (fabric.draws_on || []).join(' / ') || 'greige';
      if (!fabric.members.length) { setError(`No ${kind} fabric is available to this order's GPOs — it has to be received from the previous process first.`); return; }
      if (needsShade && !shadeId) {
        setError(`Pick the DYE SHADE at the top of the challan — the shade the vessel is run at. The Shade column on each line is only a note for the printout and does not set it.`); return;
      }
      for (let i = 0; i < clean.length; i++) {
        const m = memberOf(clean[i]);
        if (!m) { setError(`Line ${i + 1}: pick the ${kind} lot this fabric comes from, in the "${kind} lot" box under the item.`); return; }
        const b = memberBlock(m);
        if (b) { setError(`Line ${i + 1}: ${m.lot_no} cannot be drawn — ${b}.`); return; }
        if (num(clean[i].qty_sent) > num(m.available_kg) + 0.0005) {
          setError(`Line ${i + 1}: ${num(clean[i].qty_sent)} kg is more than the ${num(m.available_kg)} kg left in ${m.lot_no} (GPO ${m.gpo_no} ${m.component}). Split the line across lots, or correct the weight.`); return;
        }
      }
    }
    setBusy(true); setError('');
    try {
      const r = await api.post('/jobwork/challans/issue', {
        jwo_id: jwoId, challan_date: date, delivery_date: deliveryDate, transport, vehicle_no: vehicleNo,
        gst_pct: gstPct, against_challan: againstChallan, delivery_from: deliveryFrom,
        remarks, items: clean,
        ...(fabric.applies && needsShade ? { shade_id: Number(shadeId), machine_id: machineId || null } : {}),
      });
      // The server returns load advisories (small lot, component ratio, vendor lot no) — said, not
      // swallowed. They never block; the challan is already issued.
      if (r.data.warnings?.length) window.alert(`Issued ${r.data.data?.challan_no || ''}, with notes:\n\n• ${r.data.warnings.join('\n• ')}`);
      onSaved(r.data.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to create issue challan'); setBusy(false); }
  };
  const cell = { padding: 3 };
  const smallInput = { ...inputStyle, width: '100%', padding: '5px 6px' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '96%', maxWidth: 1000, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>📤 Material Issue Challan — {company.toUpperCase()}</h3>
        </div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>Job Work Order *</label>
            {jwos.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#9A3412' }}>
                <span>No {company.toUpperCase()} JWOs yet — create one in Job-Work first.</span>
                {canManage && <button onClick={onNewJwo} style={{ ...btnPrimary, background: '#EA580C', whiteSpace: 'nowrap' }}>+ New JWO</button>}
              </div>
            ) : (
              <select style={{ ...inputStyle, width: '100%' }} value={jwoId} onChange={e => setJwoId(e.target.value)}>
                <option value="">— select JWO —</option>
                {jwos.map(j => <option key={j.id} value={j.id}>{j.jwo_no} · {PROC_LABEL[j.process_type] || j.process_type} · {j.vendor_name}</option>)}
              </select>
            )}</div>
          <div><label style={labelStyle}>Date of Pick up *</label>
            <input type="date" required title="The date the material physically left — not the date you are keying this."
              style={{ ...inputStyle, width: '100%', ...(date ? {} : { borderColor: '#E0B4B4' }) }} value={date} onChange={e => setDate(e.target.value)} /></div>
          <div><label style={labelStyle}>Delivery Date</label>
            <input type="date" style={{ ...inputStyle, width: '100%' }} value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} /></div>
        </div>
        {jwo && <div style={{ fontSize: 12, color: '#3B3A8F', background: '#EEEDF8', borderRadius: 6, padding: '7px 10px', marginBottom: 12 }}>
          Job-Worker: <strong>{jwo.vendor_name}</strong> · GPO {jwo.gpo_no || '—'} · Process {PROC_LABEL[jwo.process_type] || jwo.process_type}
        </div>}
        {needsShade && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12, background: '#FFF9F0', border: '1px solid #F0DDBE', borderRadius: 8, padding: '10px 12px' }}>
            <div><label style={labelStyle}>Dye shade * <span style={{ textTransform: 'none', color: '#999', fontWeight: 400 }}>(the shade the vessel runs at — from this GPO's FRS)</span></label>
              <select style={{ ...inputStyle, width: '100%', borderColor: shadeId ? '#ddd' : '#E0B4B4' }} value={shadeId}
                onChange={e => applyShade(e.target.value)}>
                <option value="">{shadeOptions.length ? '— pick the shade —' : '— this GPO has no clean FRS shade —'}</option>
                {shadeOptions.map(id => <option key={id} value={id}>{shadeName(id)}</option>)}
              </select>
              {!shadeOptions.length && fabric.members.length > 0 && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 3 }}>
                No clean shade on this GPO's FRS, so no vessel can be loaded for it. Fix the FRS shade in Procurement → flagged shades first.</div>}</div>
            <div><label style={labelStyle}>Machine <span style={{ textTransform: 'none', color: '#999', fontWeight: 400 }}>(optional — blank picks the smallest that holds the load)</span></label>
              <select style={{ ...inputStyle, width: '100%' }} value={machineId} onChange={e => setMachineId(e.target.value)}>
                <option value="">{fabric.machines.length ? 'auto — smallest that fits' : '— no machines registered for this dyer —'}</option>
                {fabric.machines.map(m => <option key={m.id} value={m.id}>{m.machine_name} · loads {num(m.loading_capacity_kg)} kg{num(m.min_lot_kg) ? ` (min ${num(m.min_lot_kg)})` : ''}</option>)}
              </select></div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>Delivery From</label><input style={{ ...inputStyle, width: '100%' }} value={deliveryFrom} onChange={e => setDeliveryFrom(e.target.value)} placeholder="dispatch location" /></div>
          <div><label style={labelStyle}>Against Challan</label><input style={{ ...inputStyle, width: '100%' }} value={againstChallan} onChange={e => setAgainstChallan(e.target.value)} placeholder="ref. challan no" /></div>
          <div><label style={labelStyle}>Vehicle No</label><input style={{ ...inputStyle, width: '100%' }} value={vehicleNo} onChange={e => setVehicleNo(e.target.value)} placeholder="e.g. MH-01-AB-1234" /></div>
        </div>
        <label style={labelStyle}>Items Sent {jwo && <span style={{ textTransform: 'none', color: '#aaa' }}>(auto-pulled from JWO — edit/add as needed)</span>}</label>
        <div style={{ overflowX: 'auto', marginBottom: 8 }}>
          <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>
              {[poolApplies ? 'Yarn (from the issue pool)' : 'Item / Material', 'Description', 'GSM/Dia', needsShade ? 'Shade (note)' : 'Shade', 'Lot No', 'Rolls', 'Unit', 'Qty Sent', 'Rate ₹', 'Value ₹', ''].map(h => <th key={h} style={{ padding: '6px 6px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td style={cell}>
                    {poolApplies ? (() => {
                      const p = poolOf(it);
                      const over = p && num(it.qty_sent) > num(p.available_kg);
                      return (
                        <div style={{ minWidth: 230 }}>
                          <select style={{ ...smallInput, minWidth: 230, borderColor: it.sourcing_line_id ? '#ddd' : '#E0B4B4' }}
                            value={it.sourcing_line_id ? `${it.gpo_no}|${it.sourcing_line_id}` : ''}
                            onChange={e => pickYarn(i, e.target.value)}>
                            <option value="">— pick the yarn —</option>
                            {pool.map(x => (
                              <option key={poolKey(x)} value={poolKey(x)}>
                                GPO {x.gpo_no} · {[x.yarn_count, x.yarn_blend].filter(Boolean).join(' ')}{x.shade ? ` · ${x.shade}` : ''} · {num(x.available_kg).toLocaleString('en-IN')} kg free
                              </option>
                            ))}
                          </select>
                          {p && <div style={{ fontSize: 10, marginTop: 2, color: over ? '#A32D2D' : '#777' }}>
                            {over ? `only ${num(p.available_kg).toLocaleString('en-IN')} kg available` : `${num(p.available_kg).toLocaleString('en-IN')} kg available`}
                          </div>}
                          {!pool.length && <div style={{ fontSize: 10, marginTop: 2, color: '#A32D2D' }}>No yarn left to issue for this order's GPO.</div>}
                        </div>
                      );
                    })() : <input style={{ ...smallInput, minWidth: 120 }} value={it.item} onChange={e => setItem(i, { item: e.target.value })} />}
                    {fabric.applies && (() => {
                      const m = memberOf(it); const b = memberBlock(m);
                      const over = m && num(it.qty_sent) > num(m.available_kg) + 0.0005;
                      const kind = (fabric.draws_on || []).join(' / ') || 'greige';
                      return (
                        <div style={{ marginTop: 4, minWidth: 230 }}>
                          <div style={{ fontSize: 9.5, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{kind} lot *</div>
                          <select style={{ ...smallInput, minWidth: 230, borderColor: m && !b ? '#ddd' : '#E0B4B4' }} value={it.greige_member_id || ''}
                            onChange={e => setItem(i, { greige_member_id: e.target.value })}>
                            <option value="">— pick the lot this fabric comes from —</option>
                            {fabric.members.map(x => { const xb = memberBlock(x);
                              return <option key={x.id} value={x.id} disabled={!!xb}>{memberLabel(x)}{xb ? ` — ${xb}` : x.qc_result === 'pass' ? ' · QC pass' : x.qc_released_by ? ' · released' : ''}</option>; })}
                          </select>
                          {m && <div style={{ fontSize: 10, marginTop: 2, color: b || over ? '#A32D2D' : '#166534' }}>
                            {b ? `Cannot be drawn: ${b}.` : over ? `${num(it.qty_sent)} kg is more than the ${num(m.available_kg)} kg left in this lot.` : `QC ${m.qc_result === 'pass' ? 'pass' : 'released'} · ${num(m.available_kg)} kg available`}
                          </div>}
                          {!fabric.members.length && <div style={{ fontSize: 10, marginTop: 2, color: '#A32D2D' }}>No {kind} fabric available to this order's GPOs yet.</div>}
                          {fabric.members.length > 0 && fabric.members.every(x => memberBlock(x)) &&
                            <div style={{ fontSize: 10, marginTop: 2, color: '#A32D2D' }}>Every lot is waiting on Greige QC — nothing can be dyed until Quality records a verdict.</div>}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={cell}><input style={{ ...smallInput, minWidth: 110 }} value={it.description || ''} onChange={e => setItem(i, { description: e.target.value })} /></td>
                  <td style={cell}><input style={{ ...smallInput, width: 70 }} value={it.gsm_dia || ''} onChange={e => setItem(i, { gsm_dia: e.target.value })} /></td>
                  <td style={cell}><input style={{ ...smallInput, width: 96 }} title={it.shade || ''} value={it.shade || ''} onChange={e => setItem(i, { shade: e.target.value, shade_auto: false })} /></td>
                  <td style={cell}><input style={{ ...smallInput, width: 70 }} value={it.lot_no || ''} onChange={e => setItem(i, { lot_no: e.target.value })} /></td>
                  <td style={cell}><input type="number" style={{ ...smallInput, width: 56 }} value={it.no_of_rolls || ''} onChange={e => setItem(i, { no_of_rolls: e.target.value })} /></td>
                  <td style={cell}><input style={{ ...smallInput, width: 56 }} value={it.unit || ''} onChange={e => setItem(i, { unit: e.target.value })} /></td>
                  <td style={cell}><input type="number" step="0.001" style={{ ...smallInput, width: 76 }} value={it.qty_sent} onChange={e => setItem(i, { qty_sent: e.target.value })} /></td>
                  <td style={cell}><input type="number" step="0.01" style={{ ...smallInput, width: 76 }} value={it.rate || ''} onChange={e => setItem(i, { rate: e.target.value })} /></td>
                  <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{(num(it.qty_sent) * num(it.rate)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td style={{ ...cell, width: 24 }}>{items.length > 1 && <button onClick={() => setItems(its => its.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer' }}>✕</button>}</td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={11} style={{ padding: 10, color: '#aaa', textAlign: 'center' }}>Select a JWO to auto-pull its material.</td></tr>}
            </tbody>
          </table>
        </div>
        <button onClick={() => setItems(its => [...its, blankItem()])} style={{ padding: '5px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>+ Add Item</button>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 14, alignItems: 'start' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Transport (carrier)</label><input style={{ ...inputStyle, width: '100%' }} value={transport} onChange={e => setTransport(e.target.value)} placeholder="transporter name" /></div>
            <div><label style={labelStyle}>GST %</label><input type="number" step="0.01" style={{ ...inputStyle, width: '100%' }} value={gstPct} onChange={e => setGstPct(e.target.value)} placeholder="e.g. 5" /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Remarks</label><input style={{ ...inputStyle, width: '100%' }} value={remarks} onChange={e => setRemarks(e.target.value)} /></div>
          </div>
          <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '10px 14px', fontSize: 12.5 }}>
            <Row2 label="Subtotal (taxable)" value={inr(gst.subtotal)} />
            <Row2 label={`CGST @ ${(gst.pct / 2) || 0}%`} value={inr(gst.cgst)} />
            <Row2 label={`SGST @ ${(gst.pct / 2) || 0}%`} value={inr(gst.sgst)} />
            <div style={{ borderTop: '1px solid #ddd', marginTop: 6, paddingTop: 6 }}><Row2 label="Grand Total" value={inr(gst.grandTotal)} bold /></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={{ ...btnPrimary, background: '#E22D2D' }}>{busy ? 'Saving…' : '📤 Issue & Print'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Receipt Challan: pick an issue challan → shows sent → enter received → auto-balance ──
function ReceiptChallanModal({ company, onClose, onSaved }) {
  const [issues, setIssues] = useState([]);
  const [issueId, setIssueId] = useState('');
  const [date, setDate] = useState('');   // blank, not today — see the issue modal
  const [receivedBy, setReceivedBy] = useState('');
  const [transport, setTransport] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [components, setComponents] = useState([]);
  const [declLines, setDeclLines] = useState([]);   // the GPO's declared FRS lines, for the picker

  useEffect(() => {
    // status=issued: a cancelled challan is a record, not an offer. Nine of the fourteen Deroi
    // issue challans are cancelled, and the option text is "{challan_no} · {jwo_no} · {vendor}"
    // with no status — so JWIC-DEROI-0015 (cancelled) and JWIC-DEROI-0028 (live) read identically
    // apart from the number, for the same order and the same vendor. The route refuses a retired
    // parent too; this stops anyone reaching the refusal by accident.
    api.get(`/jobwork/challans?jwo_type=${company}&type=issue&status=issued`).then(r => setIssues(r.data.data || [])).catch(() => {});
    api.get('/procurement/fabric-components').then(r => setComponents(r.data.data || [])).catch(() => {});
  }, [company]);
  const issue = issues.find(c => c.id === issueId);
  // A knitting receipt makes a greige lot, and a lot is PER COMPONENT — body and collar are
  // weighed separately because they are knitted separately and cost differently.
  // THE ORDER SAYS WHAT WORK THIS IS. The old test — "do the lines carry a sourcing_line_id" —
  // hid the component column whenever they did not, and the backend then refused the receipt for
  // a field the form never showed. jwo_process_type comes off the challans list (step 5).
  const makesLots = company === 'deroi' && issue?.jwo_process_type === 'knitting';
  useEffect(() => {
    const gpo = issue?.gpo_no || (issue?.items || [])[0]?.gpo_no;
    if (!gpo) { setDeclLines([]); return; }
    api.get(`/procurement/gpo-components?gpo_no=${encodeURIComponent(gpo)}`)
      .then(r => setDeclLines((r.data.lines || []).filter(l => l.declaration)))
      .catch(() => setDeclLines([]));
  }, [issueId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!issue) { setItems([]); return; }
    // Carry the per-line detail (description/rolls/shade/lot/GSM) forward from the
    // issue line so the receipt records the same goods; only qty_received is entered.
    setItems((issue.items || []).map((it, i) => ({
      item: it.item, description: it.description || '', unit: it.unit, qty_sent: num(it.qty_sent), qty_received: '',
      no_of_rolls: it.no_of_rolls ?? '', shade: it.shade || '', lot_no: it.lot_no || '', gsm_dia: it.gsm_dia || '',
      // THE IDENTITY TRAVELS (mig 294/295). The receipt creates the greige lot, and a lot member
      // carries the yarn it was knit from — which only the issue line knows.
      gpo_no: it.gpo_no || issue.gpo_no || '', sourcing_line_id: it.sourcing_line_id || '',
      yarn_master_id: it.yarn_master_id || '', shade_id: it.shade_id || '',
      component: it.component || '', material_kind: it.material_kind || '',
      frs_line_id: '',                 // picked only where the GPO declares this component twice
      grp: it.sourcing_line_id || `sr${i + 1}`,   // which ISSUE line this row came off
    })));
  }, [issueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setItem = (i, patch) => setItems(its => its.map((it, j) => j === i ? { ...it, ...patch } : it));
  const save = async () => {
    if (!issueId) { setError('Select an issue challan'); return; }
    if (!date) { setError('Date is required — the date the material physically came back, not today.'); return; }
    if (makesLots && items.some(it => num(it.qty_received) > 0 && !it.component)) {
      setError('Pick the component (body/collar/cuff) for every line you received — the greige lot is per component.'); return;
    }
    // Where a GPO declares one component on more than one fabric line, the line has to say which.
    // Caught here so the operator reads it next to the row, not as a 409 after the round trip.
    const needLine = items.find(it => num(it.qty_received) > 0 && it.component && !it.frs_line_id
      && declLines.filter(l => l.declaration?.component === it.component).length > 1);
    if (needLine) {
      setError(`This GPO declares "${needLine.component}" on more than one fabric line — pick which one this cloth belongs to. Choosing for you would be a guess.`); return;
    }
    // THE REFUSAL THAT STOOD HERE IS GONE (step 7, ruling 1 — ships with the route change).
    //
    // It blocked submit whenever a line weighed more than was issued, with the same sentence the
    // backend used. On GPO 1742 that is what turned 356.160 kg into 340.160 — exactly 16.000 kg
    // less, against 16 rolls — and the 356.160 was never recorded anywhere, because a refusal
    // writes nothing.
    //
    // The refusal the operator actually meets is this one, not the backend's, so removing the
    // backend check alone would have left 1742 reproducible. Over is now RECORDED AND HELD: the
    // weight is saved and the lot and cost wait for a reason. Nothing here blocks it.
    setBusy(true); setError('');
    try {
      const payload = items.map(it => ({
        item: it.item, description: it.description, unit: it.unit,
        qty_sent: num(it.qty_sent), qty_received: num(it.qty_received),
        no_of_rolls: it.no_of_rolls, shade: it.shade, lot_no: it.lot_no, gsm_dia: it.gsm_dia,
        gpo_no: it.gpo_no || null, sourcing_line_id: it.sourcing_line_id || null,
        yarn_master_id: it.yarn_master_id || null, shade_id: it.shade_id || null,
        component: it.component || null, material_kind: it.material_kind || null,
        // Sent only where the operator had a choice to make (mig 324). Null everywhere else, which
        // the backend reads as "use the unique lookup", exactly as before step 7.
        frs_line_id: it.frs_line_id || null,
      }));
      const r = await api.post('/jobwork/challans/receipt', { linked_issue_challan_id: issueId, challan_date: date, received_by: receivedBy, transport, vehicle_no: vehicleNo, remarks, items: payload });
      const made = r.data.lots || [];
      // A HELD receipt is not a failure and is not silent. It succeeded — the weight is on file —
      // and what the operator needs to know is what did NOT happen and who unblocks it.
      if (r.data.excess_hold) {
        alert(`🛡 Recorded and held.\n\n${r.data.excess_message}`);
      } else if (made.length) {
        alert(`✅ Receipt recorded.\n\n${made.map(l => `${l.lot_no} · ${l.component} · GPO ${l.gpo_no}: ${l.returned_kg} kg back of ${l.issued_kg} kg issued (loss ${l.loss_kg} kg), knitting ₹${l.amount.toLocaleString('en-IN')} at ₹${l.rate}/kg`).join('\n')}`);
      }
      onSaved(r.data.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to create receipt challan'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '94%', maxWidth: 760, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>📥 Material Receipt Challan — {company.toUpperCase()}</h3>
        </div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label style={labelStyle}>Against Issue Challan *</label>
            {issues.length === 0 ? (
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#9A3412' }}>
                No {company.toUpperCase()} issue challans yet — raise a Material Issue Challan first (which itself needs a JWO).
              </div>
            ) : (
              <select style={{ ...inputStyle, width: '100%' }} value={issueId} onChange={e => setIssueId(e.target.value)}>
                <option value="">— select issue challan —</option>
                {issues.map(c => <option key={c.id} value={c.id}>{c.challan_no} · {c.jwo_no} · {c.vendor_name}</option>)}
              </select>
            )}</div>
          <div><label style={labelStyle}>Date *</label>
            <input type="date" required title="The date the material physically came back — not the date you are keying this."
              style={{ ...inputStyle, width: '100%', ...(date ? {} : { borderColor: '#E0B4B4' }) }} value={date} onChange={e => setDate(e.target.value)} /></div>
        </div>
        {issue && <div style={{ fontSize: 12, color: '#3B3A8F', background: '#EEEDF8', borderRadius: 6, padding: '7px 10px', marginBottom: 12 }}>
          Job-Worker: <strong>{issue.vendor_name}</strong> · JWO {issue.jwo_no || '—'} · Issued {fmtD(issue.challan_date)}
        </div>}
        <label style={labelStyle}>Items — detail carried from the issue; enter qty received (balance auto-computed)</label>
        <div style={{ overflowX: 'auto', marginBottom: 8 }}>
          <table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>
              {['Item / Material', ...(makesLots ? ['Component *', 'Fabric line'] : []), 'Description', 'GSM/Dia', 'Shade', 'Lot No', 'Rolls', 'Unit', 'Qty Sent', 'Qty Received', 'Balance / Shortage', ...(makesLots ? [''] : [])].map(h => <th key={h} style={{ padding: '6px 6px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((it, i) => {
                const bal = +(num(it.qty_sent) - num(it.qty_received)).toFixed(3);
                return (
                  <tr key={i}>
                    <td style={{ padding: 5 }}>{it.item}</td>
                    {makesLots && <td style={{ padding: 5 }}>
                      <select style={{ ...inputStyle, padding: '4px 6px', minWidth: 110, borderColor: it.component ? '#ddd' : '#E0B4B4' }}
                        value={it.component || ''} onChange={e => setItem(i, { component: e.target.value })}>
                        <option value="">— component —</option>
                        {components.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                    </td>}
                    {makesLots && (() => {
                      // ONLY WHERE THERE IS A CHOICE. A GPO that declares this component once has
                      // nothing to pick and the backend's lookup answers; 1838 declares interlock
                      // body in two shades and picking one is a decision, not a lookup. Labelled
                      // by SHADE, because that is what a person on the floor recognises.
                      const opts = declLines.filter(l => l.declaration?.component === it.component);
                      return <td style={{ padding: 5 }}>
                        {opts.length > 1 ? (
                          <select style={{ ...inputStyle, padding: '4px 6px', minWidth: 120, borderColor: it.frs_line_id ? '#ddd' : '#E0B4B4' }}
                            value={it.frs_line_id || ''} onChange={e => setItem(i, { frs_line_id: e.target.value })}>
                            <option value="">— which fabric line —</option>
                            {opts.map(l => <option key={l.line_id} value={l.line_id}>{l.shade || l.item_desc || l.line_id.slice(0, 8)}</option>)}
                          </select>
                        ) : <span style={{ color: '#aaa' }}>—</span>}
                      </td>;
                    })()}
                    <td style={{ padding: 5, color: '#666' }}>{it.description || '—'}</td>
                    <td style={{ padding: 5, color: '#666' }}>{it.gsm_dia || '—'}</td>
                    <td style={{ padding: 5, color: '#666' }}>{it.shade || '—'}</td>
                    <td style={{ padding: 5, color: '#666' }}>{it.lot_no || '—'}</td>
                    <td style={{ padding: 5, textAlign: 'right', color: '#666' }}>{it.no_of_rolls || '—'}</td>
                    <td style={{ padding: 5 }}>{it.unit || '—'}</td>
                    <td style={{ padding: 5, textAlign: 'right', fontWeight: 600 }}>{num(it.qty_sent).toLocaleString('en-IN')}</td>
                    <td style={{ padding: 3, width: 100 }}><input type="number" step="0.001" style={{ ...inputStyle, width: '100%', padding: '5px 6px' }} value={it.qty_received} onChange={e => setItem(i, { qty_received: e.target.value })} /></td>
                    {/* THREE OUTCOMES, NOT TWO. This was `bal > 0 ? short : '✓ nil'` — a sign
                        test with no negative branch, so an OVER-receipt rendered as a tick: 345
                        sent against 356.160 received showed "✓ nil", and the one number that
                        would have said "you have keyed more than you issued" confirmed it
                        instead. The stored balance was always signed; only the display collapsed
                        it. An excess is not nil and must not look like agreement. */}
                    <td style={{ padding: 5, textAlign: 'right', fontWeight: 700, color: bal === 0 ? '#166534' : '#A32D2D', whiteSpace: 'nowrap' }}>
                      {bal > 0 ? `${bal.toLocaleString('en-IN')} short ⚠`
                        : bal < 0 ? `${(-bal).toLocaleString('en-IN')} excess ⚠`
                        : '✓ nil'}</td>
                    {makesLots && <td style={{ padding: 5, whiteSpace: 'nowrap' }}>
                      {/* SPLIT, not "add a line". One yarn issue comes back as body, collar and
                          cuff; a split row inherits the issue line's identity (grp, yarn, GPO,
                          sourcing_line_id) so the backend can still tell they are one issue line
                          and sum them against it. An invented row with no issue behind it would
                          be a different thing entirely, and the form does not offer that. */}
                      <button type="button" title="This issue line came back as more than one component"
                        onClick={() => setItems(its => {
                          const src = its[i];
                          const copy = { ...src, qty_received: '', no_of_rolls: '', component: '', frs_line_id: '', lot_no: '' };
                          return [...its.slice(0, i + 1), copy, ...its.slice(i + 1)];
                        })}
                        style={{ padding: '3px 8px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #cfcdf0', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>+ split</button>
                      {items.filter(x => x.grp === it.grp).length > 1 && (
                        <button type="button" title="Remove this split line"
                          onClick={() => setItems(its => its.filter((_, j) => j !== i))}
                          style={{ marginLeft: 4, padding: '3px 7px', background: '#FCEBEB', color: '#A32D2D', border: '1px solid #f0cdcd', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>✕</button>
                      )}
                    </td>}
                  </tr>
                );
              })}
              {!items.length && <tr><td colSpan={13} style={{ padding: 10, color: '#aaa', textAlign: 'center' }}>Select an issue challan to load its items.</td></tr>}
            </tbody>
          </table>
        </div>
        {makesLots && items.length > 0 && (() => {
          // PER ISSUE LINE, because that is what the backend measures against. Siblings are
          // summed; the arithmetic is shown rather than just the verdict, so the operator can see
          // where a total came from before they commit to it.
          const groups = [...new Set(items.map(x => x.grp))].map(g => {
            const rows = items.filter(x => x.grp === g);
            return { g, sent: num(rows[0]?.qty_sent), got: +(rows.reduce((t, x) => t + num(x.qty_received), 0)).toFixed(3), n: rows.length };
          }).filter(x => x.got > 0);
          const over = groups.filter(x => x.got > x.sent + 0.0005);
          if (!groups.length) return null;
          return (
            <div style={{ marginBottom: 12 }}>
              {groups.map(x => (
                <div key={x.g} style={{ fontSize: 11, color: '#555', fontVariantNumeric: 'tabular-nums' }}>
                  Issued <strong>{x.sent.toLocaleString('en-IN')}</strong>
                  {' · '}receiving <strong>{x.got.toLocaleString('en-IN')}</strong>
                  {x.n > 1 ? ` across ${x.n} lines` : ''}
                  {' · '}
                  <span style={{ color: x.got > x.sent + 0.0005 ? '#B45309' : '#166534', fontWeight: 700 }}>
                    {x.got > x.sent + 0.0005 ? `${(+(x.got - x.sent).toFixed(3)).toLocaleString('en-IN')} over`
                      : x.got < x.sent - 0.0005 ? `${(+(x.sent - x.got).toFixed(3)).toLocaleString('en-IN')} short`
                      : 'exact'}
                  </span>
                </div>
              ))}
              {over.length > 0 && (
                <div style={{ marginTop: 6, background: '#FEF3E2', border: '1px solid #f0d9ae', color: '#7a4b00', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
                  🛡 <strong>This is more than was issued. It will be RECORDED and held.</strong><br />
                  The weight, challan, date, vehicle and receiver are saved now. No greige lot is created
                  and no cost is booked until management resolves the difference. Nothing is lost by
                  recording what you actually weighed — that is the point.
                </div>
              )}
            </div>
          );
        })()}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
          <div><label style={labelStyle}>Received By</label><input style={{ ...inputStyle, width: '100%' }} value={receivedBy} onChange={e => setReceivedBy(e.target.value)} placeholder="Store in-charge name" /></div>
          <div><label style={labelStyle}>Transport (carrier)</label><input style={{ ...inputStyle, width: '100%' }} value={transport} onChange={e => setTransport(e.target.value)} placeholder="transporter name" /></div>
          <div><label style={labelStyle}>Vehicle No</label><input style={{ ...inputStyle, width: '100%' }} value={vehicleNo} onChange={e => setVehicleNo(e.target.value)} placeholder="e.g. MH-01-AB-1234" /></div>
          <div><label style={labelStyle}>Remarks</label><input style={{ ...inputStyle, width: '100%' }} value={remarks} onChange={e => setRemarks(e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={{ ...btnPrimary, background: '#166534' }}>{busy ? 'Saving…' : '📥 Receive & Print'}</button>
        </div>
      </div>
    </div>
  );
}
